import { type FC, type MouseEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import { useGetGamePanelQuery } from '@/api/queries/game';
import { AppRoute } from '@/common/app/routes';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

import bingoIcon from '/img/bingo/bingo-icon.png';
import bgDice from '/img/game-center/bg-dice.png';
import character from '/img/game-center/character.png';
import diceGem from '/img/game-center/dice-gem.png';
import iconTeddy from '/img/game-center/icon-teddy.png';
import iconUsd from '/img/game-center/icon-usd.png';
import lockIcon from '/img/game-center/lock.png';
import goldTicketIcon from '/img/tap-power.png';

const formatTimeRemaining = (targetIso: string, nowMs: number): string => {
  const diff = Math.max(0, new Date(targetIso).getTime() - nowMs);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);

  return `${days}d : ${hours}h : ${minutes}m`;
};

interface GameCardProps {
  title: string;
  diceImg?: string;
  diceImgClassName?: string;
  diceCount?: string;
  to?: string;
  locked?: boolean;
  timer?: string;
  timerLabel?: string;
  burnHint?: string;
  artImg?: string;
  artClassName?: string;
  /** Live event — gold frame, shine and a corner badge to draw the eye. */
  hot?: boolean;
  badge?: string;
}

const GameCard: FC<GameCardProps> = ({
  title,
  diceImg,
  diceImgClassName,
  diceCount,
  to,
  locked,
  timer,
  timerLabel,
  burnHint,
  artImg,
  artClassName,
  hot,
  badge,
}) => {
  const { t } = useTranslation();
  const [hintOpen, setHintOpen] = useState(false);

  const handleHintToggle = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setHintOpen((prev) => !prev);
  };

  const closeHint = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setHintOpen(false);
  };

  const inner: ReactNode = (
    <>
      <div className={cn(css.cardBgGradient, hot && css.cardBgGradientHot)} />
      <div className={cn(css.cardEllipse, hot && css.cardEllipseHot)} />
      {hot && <div className={css.cardShine} />}
      {badge && (
        <span className={css.liveBadge}>
          <span className={css.liveDot} />
          {badge}
        </span>
      )}

      {artImg && (
        <img
          src={artImg}
          alt=""
          className={cn(css.cardArt, artClassName, hot && css.artHot)}
          draggable={false}
        />
      )}

      <h3 className={css.cardTitle}>{title}</h3>
      {diceImg && (
        <div className={css.dicePill}>
          <img
            src={diceImg}
            alt=""
            className={cn(css.dicePillImg, diceImgClassName)}
            draggable={false}
          />
          {diceCount && <span className={css.dicePillCount}>{diceCount}</span>}
        </div>
      )}

      {timer && (
        <div className={css.timer}>
          <div className={css.timerLabelRow}>
            <span className={css.timerLabel}>{timerLabel ?? t('Burn')}</span>
            {burnHint && (
              <button
                type="button"
                className={css.hintBtn}
                onClick={handleHintToggle}
                aria-label={t('Info')}
              >
                <img
                  src="/img/bingo/info-icon.svg"
                  alt=""
                  className={css.hintIcon}
                  draggable={false}
                />
              </button>
            )}
          </div>
          <span className={css.timerValue}>{timer}</span>
          {burnHint && hintOpen && (
            <>
              <div
                className={css.hintBackdrop}
                onClick={closeHint}
                role="presentation"
              />
              <div className={css.hintTooltip}>{burnHint}</div>
            </>
          )}
        </div>
      )}

      {locked && <span className={css.soonLabel}>{t('SOON')}</span>}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cn(css.card, hot && css.cardHot)}>
        {inner}
      </Link>
    );
  }

  return <div className={cn(css.card, locked && css.cardLocked)}>{inner}</div>;
};

const GameCenter: FC = () => {
  const { t } = useTranslation();
  const { data } = useGetGamePanelQuery();

  const craftEvent = data?.eventsDice.find((e) => e.type === 'craft');
  const tonEvent = data?.eventsDice.find((e) => e.type === 'ton');
  const bingo = data?.bingo;
  const now = data?.now ?? Date.now();

  const craftTimer = craftEvent
    ? formatTimeRemaining(craftEvent.endedAt, now)
    : undefined;
  const tonTimer = tonEvent
    ? formatTimeRemaining(tonEvent.endedAt, now)
    : undefined;

  const bingoTarget =
    bingo?.status === 'pending'
      ? bingo.startAt
      : bingo?.status === 'active'
        ? bingo.endAt
        : null;
  const bingoTimer = bingoTarget
    ? formatTimeRemaining(bingoTarget, now)
    : undefined;
  const bingoTimerLabel =
    bingo?.status === 'active' ? t('Time left') : t('Event Start');

  const isCraftActive = craftEvent?.status === 'active';
  const isTonActive = tonEvent?.status === 'active';
  const isBingoActive = bingo?.status === 'active';

  const burnHint = t(
    'All unused dice at event end are converted to Tgem at a 10:1 rate. Spend them in time!',
  );

  const diceBalance = useAppSelector(
    (state) => state.user.user?.balance.dice ?? 0,
  );
  const bingoBalls = useAppSelector(
    (state) => state.user.user?.balance.bingoBalls ?? 0,
  );

  // Game Center reads scratch state from the panel response (canonical server)
  // — falls back to the Redux event mirror for the dev/demo path before API
  // wiring is finished.
  const scratchPanel = data?.scratch ?? null;
  const scratchEvent = useAppSelector((state) => state.scratch.event);
  const scratchStatus =
    scratchPanel?.status ??
    (scratchEvent.status === 'inactive' ? 'none' : scratchEvent.status);
  const scratchTargetIso =
    scratchStatus === 'pending'
      ? (scratchPanel?.startAt ?? scratchEvent.startAt ?? null)
      : scratchStatus === 'active'
        ? (scratchPanel?.endAt ?? scratchEvent.endAt ?? null)
        : null;
  const scratchTimer = scratchTargetIso
    ? formatTimeRemaining(scratchTargetIso, now)
    : undefined;
  const scratchTimerLabel =
    scratchStatus === 'active' ? t('Time left') : t('Event Start');
  const isScratchActive = scratchStatus === 'active';

  return (
    <main className={css.page}>
      <section className={css.hero}>
        <img src={bgDice} alt="" className={css.heroBg} draggable={false} />
        <img
          src={character}
          alt=""
          className={css.character}
          draggable={false}
        />
        <div className={css.heroFade} />
      </section>

      <section className={css.cardsGrid}>
        <GameCard
          title={t('Roll Craft')}
          diceImg={diceGem}
          diceImgClassName={css.dicePillImgUpper}
          diceCount={String(diceBalance)}
          to={isCraftActive ? AppRoute.DICE_GAME : undefined}
          locked={!isCraftActive}
          timer={craftTimer}
          burnHint={burnHint}
          artImg={iconTeddy}
          artClassName={css.artTeddy}
        />
        <GameCard
          title={t('Bingo')}
          diceImg={bingoIcon}
          diceCount={String(bingoBalls)}
          to={isBingoActive ? AppRoute.BINGO : undefined}
          artImg={isBingoActive ? undefined : lockIcon}
          timer={bingoTimer}
          timerLabel={bingoTimerLabel}
          artClassName={isBingoActive ? undefined : css.artLock}
        />
        <GameCard
          title={t('Scratch')}
          to={isScratchActive ? AppRoute.SCRATCH : undefined}
          locked={scratchStatus === 'none'}
          timer={scratchTimer}
          timerLabel={scratchTimerLabel}
          artImg={goldTicketIcon}
          artClassName={css.artLock}
          hot={isScratchActive}
          badge={isScratchActive ? t('Live now') : undefined}
        />
        <GameCard
          title={t('New game')}
          locked
          timer={isTonActive ? tonTimer : undefined}
          burnHint={isTonActive ? burnHint : undefined}
          artImg={isTonActive ? iconUsd : lockIcon}
          artClassName={isTonActive ? css.artUsd : css.artLock}
        />
      </section>
    </main>
  );
};

export { GameCenter };
